import { useState, useEffect } from 'react'
import Card, { CardHeader, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import LoadingState from '../ui/LoadingState'
import { reporteService } from '../../services/reporteService'
import Chart from 'react-apexcharts'
import {
  createArtemisAxisOptions,
  createArtemisBaseOptions,
  getArtemisChartTokens,
  getArtemisPalette,
} from '../../utils/artemisApexTheme'
import { 
  Users, Heart, Calendar, FileText, Building2, User, 
  RefreshCw, AlertCircle, TrendingUp, Download 
} from 'lucide-react'

export default function EstadisticasGenerales({ idClinica = null }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    // Solo hacer fetch si idClinica está definido (incluyendo 0) o si es null explícitamente después de que el usuario se haya cargado
    // Esto evita hacer peticiones antes de que el usuario esté cargado
    if (idClinica !== undefined) {
      fetchData()
    }
  }, [idClinica])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      // IMPORTANTE: El backend resuelve automáticamente el id_clinica según el usuario autenticado
      // Solo pasamos id_clinica si es explícitamente proporcionado (para SUPERADMIN/ADMIN que quieren filtrar)
      // Para veterinarios admin (idClinica es null/undefined), NO pasamos el parámetro para que el backend lo resuelva automáticamente
      const params = {}
      // Solo agregar id_clinica si es un número válido mayor que 0 (no null, no undefined, no 0)
      // null y undefined significan "dejar que el backend lo resuelva automáticamente"
      // Usamos != null para capturar tanto null como undefined
      if (idClinica != null && !isNaN(Number(idClinica)) && Number(idClinica) > 0) {
        params.id_clinica = Number(idClinica)
      }
      
      // Debug: verificar qué parámetros se están enviando
      console.log('🔍 EstadisticasGenerales - Parámetros enviados:', { 
        idClinica, 
        idClinicaType: typeof idClinica,
        params,
        willPassIdClinica: idClinica != null && !isNaN(Number(idClinica)) && Number(idClinica) > 0
      })
      
      const response = await reporteService.getEstadisticas(params)
      if (response.success) {
        setData(response.data)
      } else {
        setError(response.error || 'Error al cargar estadísticas')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('❌ Error en EstadisticasGenerales:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando estadísticas..." size="lg" />
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button onClick={fetchData} variant="secondary" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return null
  }

  const tokens = getArtemisChartTokens()
  const axisOptions = createArtemisAxisOptions()
  const palette = getArtemisPalette()

  // Configuración gráfico mascotas por especie
  const pieBaseOptions = createArtemisBaseOptions({
    type: 'pie',
    colors: palette.slice(0, 5),
  })
  const chartEspeciesOptions = {
    ...pieBaseOptions,
    labels: data.distribuciones.mascotasPorEspecie.map(item => item.especie),
    stroke: {
      width: 2,
      colors: [tokens.isDark ? '#1a2535' : '#ffffff'],
    },
    legend: {
      ...pieBaseOptions.legend,
      position: 'bottom',
    },
  }

  const chartEspeciesSeries = data.distribuciones.mascotasPorEspecie.map(item => item.cantidad)

  // Configuración gráfico citas por estado
  const barBaseOptions = createArtemisBaseOptions({
    type: 'bar',
    height: 350,
    colors: [palette[1]],
  })
  const chartCitasEstadoOptions = {
    ...barBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.distribuciones.citasPorEstado.map(item => item.estado?.replace('_', ' ') || 'Sin estado'),
      labels: {
        ...axisOptions.labels,
        rotate: -20,
      },
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '52%',
        horizontal: false,
      }
    },
    grid: {
      ...barBaseOptions.grid,
      padding: { top: 4, right: 10, left: 0, bottom: 0 },
    },
  }

  const chartCitasEstadoSeries = [{
    name: 'Citas',
    data: data.distribuciones.citasPorEstado.map(item => item.cantidad)
  }]

  // Configuración gráfico crecimiento
  const lineBaseOptions = createArtemisBaseOptions({
    type: 'line',
    height: 350,
    colors: [palette[0], palette[1], palette[3], palette[4]],
  })
  const chartCrecimientoOptions = {
    ...lineBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.crecimientoMensual.map(item => item.mesCorto),
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 3,
      strokeWidth: 0,
      hover: {
        size: 5,
      },
    },
    grid: {
      ...lineBaseOptions.grid,
      padding: { top: 8, right: 10, left: 0, bottom: 0 },
    },
    legend: {
      ...lineBaseOptions.legend,
      position: 'top',
    },
  }

  const chartCrecimientoSeries = [
    {
      name: 'Usuarios',
      data: data.crecimientoMensual.map(item => item.usuarios)
    },
    {
      name: 'Mascotas',
      data: data.crecimientoMensual.map(item => item.mascotas)
    },
    {
      name: 'Citas',
      data: data.crecimientoMensual.map(item => item.citas)
    },
    {
      name: 'Fichas',
      data: data.crecimientoMensual.map(item => item.fichas)
    }
  ]

  // Configuración gráfico clínicas por ciudad
  const horizontalBarBaseOptions = createArtemisBaseOptions({
    type: 'bar',
    height: 350,
    colors: [palette[2]],
  })
  const chartCiudadesOptions = {
    ...horizontalBarBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.distribuciones.clinicasPorCiudad.map(item => item.ciudad),
    },
    yaxis: {
      ...axisOptions,
    },
    plotOptions: {
      bar: {
        borderRadius: 7,
        barHeight: '50%',
        horizontal: true,
      }
    },
    grid: {
      ...horizontalBarBaseOptions.grid,
      padding: { top: 4, right: 10, left: 0, bottom: 0 },
    },
  }

  const chartCiudadesSeries = [{
    name: 'Clínicas',
    data: data.distribuciones.clinicasPorCiudad.map(item => item.cantidad)
  }]

  return (
    <div className="space-y-6">
      {/* Totales */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Usuarios</p>
                <p className="text-2xl font-bold text-pastel-ink dark:text-white mt-1">{data.totales.usuarios}</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Mascotas</p>
                <p className="text-2xl font-bold text-pastel-ink dark:text-white mt-1">{data.totales.mascotas}</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Heart className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Citas</p>
                <p className="text-2xl font-bold text-pastel-ink dark:text-white mt-1">{data.totales.citas}</p>
              </div>
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <Calendar className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Fichas</p>
                <p className="text-2xl font-bold text-pastel-ink dark:text-white mt-1">{data.totales.fichas}</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Clínicas</p>
                <p className="text-2xl font-bold text-pastel-ink dark:text-white mt-1">{data.totales.clinicas}</p>
              </div>
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <Building2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Veterinarios</p>
                <p className="text-2xl font-bold text-pastel-ink dark:text-white mt-1">{data.totales.veterinarios}</p>
              </div>
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <User className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hoy */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Actividad de Hoy</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Citas</span>
                <span className="text-lg font-semibold text-pastel-ink dark:text-white">{data.hoy.citas}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Fichas</span>
                <span className="text-lg font-semibold text-pastel-ink dark:text-white">{data.hoy.fichas}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Usuarios Nuevos</span>
                <span className="text-lg font-semibold text-pastel-ink dark:text-white">{data.hoy.usuariosNuevos}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Mascotas por Especie</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartEspeciesOptions}
              series={chartEspeciesSeries}
              type="pie"
              height={350}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Citas por Estado</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartCitasEstadoOptions}
              series={chartCitasEstadoSeries}
              type="bar"
              height={350}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Crecimiento Mensual (Últimos 6 meses)</h3>
        </CardHeader>
        <CardContent>
          <Chart
            options={chartCrecimientoOptions}
            series={chartCrecimientoSeries}
            type="line"
            height={350}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Clínicas por Ciudad</h3>
            <Button variant="secondary" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Chart
            options={chartCiudadesOptions}
            series={chartCiudadesSeries}
            type="bar"
            height={350}
          />
        </CardContent>
      </Card>
    </div>
  )
}

